import React from 'react';
import { formatStepDistance } from '../../utils/routeParser';

/**
 * DirectionStep - Renders a single turn-by-turn direction step.
 * 
 * For regular steps:
 *   [icon]  [distance (bold)]
 *           [instruction text]
 * 
 * For waypoint (midway destination) steps:
 *   [black circle + icon]  [name (bold)]
 *                          [address text]
 * 
 * For final destination:
 *   [where-to-vote icon]  [name (bold)]
 *                         [address text]
 * 
 * With a vertical connector line between steps.
 */
const DirectionStep = ({ step, index, isActive, isLast, onHover, onLeave, onClick }) => {
  if (!step) return null;

  const distanceText = formatStepDistance(step.distance);

  // Waypoint (intermediate destination) rendering
  if (step.isWaypoint) {
    return (
      <div
        className={`direction-step direction-step-waypoint ${isActive ? 'direction-step-active' : ''}`}
        onMouseEnter={() => onHover && onHover(index)}
        onMouseLeave={() => onLeave && onLeave(index)}
        role="listitem"
        aria-label={`Stop: ${step.instruction}`}
      >
        {/* Icon: black circle with + */}
        <div className="direction-step-icon direction-step-icon-waypoint">
          <img
            src={step.icon}
            alt=""
            aria-hidden="true"
            draggable={false}
          />
        </div>

        {/* Text: name + address */}
        <div className="direction-step-content">
          <span className="direction-step-waypoint-name">{step.instruction}</span>
          {step.address && (
            <span className="direction-step-waypoint-address">{step.address}</span>
          )}
        </div>
      </div>
    );
  }

  // Final destination rendering
  if (step.isFinalDestination) {
    return (
      <div
        className={`direction-step direction-step-final-destination ${isActive ? 'direction-step-active' : ''}`}
        onMouseEnter={() => onHover && onHover(index)}
        onMouseLeave={() => onLeave && onLeave(index)}
        role="listitem"
        aria-label={`Destination: ${step.instruction}`}
      >
        {/* Icon: black circle with where-to-vote */}
        <div className="direction-step-icon direction-step-icon-final-dest">
          <img
            src={step.icon}
            alt=""
            aria-hidden="true"
            draggable={false}
          />
        </div>

        {/* Text: name + address */}
        <div className="direction-step-content">
          <span className="direction-step-waypoint-name">{step.instruction}</span>
          {step.address && (
            <span className="direction-step-waypoint-address">{step.address}</span>
          )}
        </div>
      </div>
    );
  }

  // Regular maneuver step rendering
  return (
    <div
      className={`direction-step ${isActive ? 'direction-step-active' : ''}`}
      onMouseEnter={() => onHover && onHover(index)}
      onMouseLeave={() => onLeave && onLeave(index)}
      onClick={() => onClick && onClick(index)}
      role="listitem"
      aria-label={step.instruction}
    >
      {/* Left column: icon + connector */}
      <div className="direction-step-left">
        <div className={`direction-step-icon ${step.isDepart ? 'direction-step-icon-depart' : ''} ${step.isArrive ? 'direction-step-icon-arrive' : ''}`}>
          <img
            src={step.icon}
            alt=""
            aria-hidden="true"
            draggable={false}
          />
        </div>
        {!isLast && <div className="direction-step-line" />}
      </div>

      {/* Right column: distance + instruction */}
      <div className="direction-step-content">
        {distanceText && (
          <span className="direction-step-distance">{distanceText}</span>
        )}
        <span className="direction-step-instruction">{step.instruction?.replace(/\.$/, '')}</span>
      </div>
    </div>
  );
};

export default DirectionStep;
